import { useEffect, useMemo, useState } from "react"
import { useCategorySelection } from "hooks/categorySelection.hook"
import { useCustomizationFields } from "components/ui/customWebviewer/shared/CustomizationSection/useCustomizationFields.hook"
import {
	isCountertop,
	isCustomFurniture,
	isOtherFurniture,
} from "components/ui/customWebviewer/FurnitureModelLabel/furnitureModel.helpers"
import {
	CUSTOM_TOP_LEVEL_CATEGORY,
	OTHER_TOP_LEVEL_CATEGORY,
	SYNTHETIC_TOP_LEVEL_OPTIONS,
} from "./furniture.constants"
import type { TFurniture, TSelectedModelNumber } from "types/elevationReferences.type"
import { useMutationQueueStore } from "store/mutationQueue.store"
import { furnitureQueueKey } from "./furnitureQueueKey"
import type { TCustomModelNumberSummary } from "types/customModelNumbers.type"

/**
 * What the sidebar renders. `custom` (#1346) is an object priced from a drawing-local Custom Model
 * Number: like `other` it has no category, but unlike it, it HAS a model number and mandatory
 * dimensions.
 */
export type TFurnitureVariant = "countertop" | "custom" | "other" | "typical"

export const useFurnitureForm = (furniture: TFurniture) => {
	// Backend marks "Other" by clearing all three category levels, and a custom object (#1346) by its
	// `custom_object_config` — which also has no category, hence the helper rather than a null check.
	const isCustomFromBackend = isCustomFurniture(furniture)
	const isOtherFromBackend = isOtherFurniture(furniture)

	const defaultTopLevelCategory = isCustomFromBackend
		? CUSTOM_TOP_LEVEL_CATEGORY
		: isOtherFromBackend
			? OTHER_TOP_LEVEL_CATEGORY
			: (furniture.top_level_category?.name ?? null)

	const categorySelection = useCategorySelection({
		defaultTopLevelCategory,
		defaultSuperCategory: furniture.super_category?.name ?? null,
		defaultModelCategoryId: furniture.category?.id ?? null,
		defaultModelCategoryName: furniture.category?.name ?? null,
		extraTopLevelOptions: SYNTHETIC_TOP_LEVEL_OPTIONS,
	})

	const [selectedErefId, setSelectedErefId] = useState<number | null>(furniture.eref_id)
	const [width, setWidth] = useState<number | null>(furniture.width)
	const [height, setHeight] = useState<number | null>(furniture.height)
	const [depth, setDepth] = useState<number | null>(furniture.depth)
	const [adjustableShelves, setAdjustableShelves] = useState<number | null>(
		furniture.estimation_config?.adjusted_shelves ?? null,
	)
	const [hasLocks, setHasLocks] = useState<"yes" | "no">(furniture.estimation_config?.has_locks ? "yes" : "no")
	const [fillings, setFillings] = useState<number>(furniture.estimation_config?.number_of_fillers ?? 0)
	const [finishingEnds, setFinishingEnds] = useState<number>(
		furniture.estimation_config?.number_of_finishing_ends ?? 0,
	)
	// REQ-449. `number | null` like `adjustableShelves` — `NumberInputRow` is a free-entry field
	// that goes empty (`null`) mid-edit — but the backend value itself defaults to 0 and cannot be
	// negative (auto-scan-backend#2181), so every resync below falls back to 0, never null.
	const [grommets, setGrommets] = useState<number | null>(furniture.countertop_attachments?.grommets ?? 0)
	const [wallCleats, setWallCleats] = useState<number | null>(furniture.countertop_attachments?.wall_cleats ?? 0)
	const [price, setPrice] = useState<number | null>(furniture.fixed_price_config?.fixed_price ?? null)
	const [name, setName] = useState<string>(furniture.fixed_price_config?.name ?? "")
	const [notes, setNotes] = useState<string>(furniture.estimation_config_comment ?? "")
	// REQ-413/414. Seeded from the object itself, which is what makes the saved note and amount
	// come back — expanded — on reopen and on reload: `customization` rides along with the normal
	// quotation payload, so there is nothing extra to fetch.
	const customization = useCustomizationFields(furniture.customization)
	const [selectedModelNumber, setSelectedModelNumber] = useState<TSelectedModelNumber | null>(
		furniture.selected_model_number,
	)
	/**
	 * The Custom Model Number linked to a custom object (#1346), kept beside `selectedModelNumber`
	 * rather than folded into it: the two come from different endpoints, only one of them can be set
	 * at a time, and the chip has to read the right one.
	 */
	const [customModelNumber, setCustomModelNumber] = useState<TCustomModelNumberSummary | null>(
		furniture.custom_object_config?.custom_model_number ?? null,
	)

	const status = furniture.status ?? "work_in_progress"
	const isReviewed = status === "reviewed"
	const isInQuotation = status === "in_quotation"

	const incomingSelectedId = furniture.selected_model_number?.id ?? null
	useEffect(() => {
		setSelectedModelNumber(furniture.selected_model_number)
		// Only resync when the *id* changes to avoid loops on object-identity churn from cache splices.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [incomingSelectedId])

	// Peer-window resyncs (#1430) skip a change that lands while this object's own saves are queued:
	// it is an EARLIER queued commit's echo and would flip the field back to an intermediate value.
	// Read at effect time, not subscribed to — see useServerResync for why the drain must not re-run
	// these (the prop lags the drain by a render).
	const furnitureKey = furnitureQueueKey(furniture.id)
	const isSaving = () => (useMutationQueueStore.getState().pendingByKey[furnitureKey] ?? 0) > 0

	useEffect(() => {
		if (!isSaving()) setWidth(furniture.width)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [furniture.width])

	useEffect(() => {
		if (!isSaving()) setHeight(furniture.height)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [furniture.height])

	useEffect(() => {
		if (!isSaving()) setDepth(furniture.depth)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [furniture.depth])

	// Same rule for the custom link: resync on the id, not on object identity. Converting an object
	// away from custom nulls the config, which lands here as `null` and clears the chip.
	const incomingCustomModelNumberId = furniture.custom_object_config?.custom_model_number?.id ?? null
	useEffect(() => {
		setCustomModelNumber(furniture.custom_object_config?.custom_model_number ?? null)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [incomingCustomModelNumberId])

	/**
	 * Resync the three category selects when the BACKEND moves the furniture's category.
	 * `useCategorySelection` seeds its state from the `default*` params once, so a
	 * server-driven move left the sidebar showing the old category until the component
	 * remounted — the visible case being a cross-category model number saved through
	 * `select-arbitrary-model-number/`, which relocates `related_category` as part of the
	 * same transaction.
	 *
	 * `syncDefaults`'s identity changes only when one of the backend category values does,
	 * so this fires on exactly those transitions and not on the object-identity churn of a
	 * cache splice. It also cannot clobber a manual edit in progress: picking a top-level
	 * or super category only moves LOCAL state (nothing is committed until the model
	 * category is chosen), which leaves these defaults — and therefore `syncDefaults` —
	 * untouched.
	 */
	const { syncDefaults: syncCategoryDefaults } = categorySelection
	useEffect(() => {
		syncCategoryDefaults()
	}, [syncCategoryDefaults])

	const incomingHasLocks = furniture.estimation_config?.has_locks ?? false
	useEffect(() => {
		setHasLocks(incomingHasLocks ? "yes" : "no")
	}, [incomingHasLocks])

	const incomingFillers = furniture.estimation_config?.number_of_fillers ?? 0
	useEffect(() => {
		if (!isSaving()) setFillings(incomingFillers)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [incomingFillers])

	const incomingFinishingEnds = furniture.estimation_config?.number_of_finishing_ends ?? 0
	useEffect(() => {
		if (!isSaving()) setFinishingEnds(incomingFinishingEnds)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [incomingFinishingEnds])

	useEffect(() => {
		if (!isSaving()) setSelectedErefId(furniture.eref_id)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [furniture.eref_id])

	const incomingAdjustedShelves = furniture.estimation_config?.adjusted_shelves ?? null
	useEffect(() => {
		setAdjustableShelves(incomingAdjustedShelves)
	}, [incomingAdjustedShelves])

	const incomingGrommets = furniture.countertop_attachments?.grommets ?? 0
	useEffect(() => {
		setGrommets(incomingGrommets)
	}, [incomingGrommets])

	const incomingWallCleats = furniture.countertop_attachments?.wall_cleats ?? 0
	useEffect(() => {
		setWallCleats(incomingWallCleats)
	}, [incomingWallCleats])

	const incomingFixedPrice = furniture.fixed_price_config?.fixed_price ?? null
	useEffect(() => {
		setPrice(incomingFixedPrice)
	}, [incomingFixedPrice])

	const incomingName = furniture.fixed_price_config?.name ?? ""
	useEffect(() => {
		setName(incomingName)
	}, [incomingName])

	const isCountertopItem = isCountertop(furniture)

	/**
	 * Custom is tested FIRST, and against the SELECTED top level rather than the saved object: the
	 * dropdown is how an object is converted, so the panel has to switch the moment the user picks
	 * `Custom` — before the PATCH lands and while `isCountertopItem` still reads the old category.
	 */
	const variant: TFurnitureVariant = useMemo(() => {
		if (categorySelection.selectedTopLevelCategory === CUSTOM_TOP_LEVEL_CATEGORY) return "custom"
		if (isCountertopItem) return "countertop"
		if (categorySelection.selectedTopLevelCategory === OTHER_TOP_LEVEL_CATEGORY) return "other"
		return "typical"
	}, [categorySelection.selectedTopLevelCategory, isCountertopItem])

	const hasModelNumber = selectedModelNumber !== null
	const hasCustomModelNumber = customModelNumber !== null

	const isValid = useMemo(() => {
		if (selectedErefId === null || categorySelection.selectedTopLevelCategory === null) return false

		if (variant === "other") return price !== null && name.trim() !== ""

		const dimensionsFilled = width !== null && height !== null && depth !== null
		if (!dimensionsFilled) return false

		// A custom object has no category to fill in, and its price comes from the linked definition —
		// so the definition IS the remaining requirement, alongside the mandatory dimensions above.
		if (variant === "custom") return hasCustomModelNumber

		const fullCategoriesFilled =
			categorySelection.selectedSuperCategory !== null && categorySelection.selectedModelCategoryId !== null
		if (!fullCategoriesFilled) return false

		if (variant === "countertop") return true
		return hasModelNumber
	}, [
		categorySelection.selectedModelCategoryId,
		categorySelection.selectedSuperCategory,
		categorySelection.selectedTopLevelCategory,
		depth,
		hasCustomModelNumber,
		hasModelNumber,
		height,
		name,
		price,
		selectedErefId,
		variant,
		width,
	])

	return {
		variant,
		isCountertopItem,
		hasModelNumber,
		hasCustomModelNumber,
		customModelNumber,
		setCustomModelNumber,
		categorySelection,
		selectedErefId,
		setSelectedErefId,
		width,
		setWidth,
		height,
		setHeight,
		depth,
		setDepth,
		adjustableShelves,
		setAdjustableShelves,
		hasLocks,
		setHasLocks,
		fillings,
		setFillings,
		finishingEnds,
		setFinishingEnds,
		grommets,
		setGrommets,
		wallCleats,
		setWallCleats,
		price,
		setPrice,
		name,
		setName,
		notes,
		setNotes,
		customization,
		isReviewed,
		isInQuotation,
		selectedModelNumber,
		setSelectedModelNumber,
		isValid,
	}
}
